import { ReactNode } from "react";
import { RiHandCoinLine } from "@remixicon/react";
import { LEGACY_PRICING_STANDARD_RATES } from "utils/legacyRate";

export type LegacyCalloutVariant = "box" | "inline";

type LegacyCalloutProps = {
  title: string;
  children: ReactNode;
  /** `box` for sidebars and cards, `inline` for a one-line chip next to another chip. */
  variant?: LegacyCalloutVariant;
  className?: string;
  testId?: string;
};

/**
 * Green "legacy" callout shared by the four ENG-1112 surfaces. The copy is
 * supplied by the two wrappers below; this only owns the look.
 */
export function LegacyCallout({ title, children, variant = "box", className = "", testId }: LegacyCalloutProps) {
  if (variant === "inline") {
    return (
      <div
        data-testid={testId}
        className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded bg-success-50 text-success-800 ${className}`}
      >
        <RiHandCoinLine className="size-4 flex-shrink-0 text-success-600" aria-hidden="true" />
        <span>
          <span className="font-semibold">{title}</span> {children}
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      className={`flex items-start gap-3 p-3 rounded-md border bg-success-50 border-success-100 text-success-800 ${className}`}
    >
      <RiHandCoinLine className="size-6 flex-shrink-0 text-success-600" aria-hidden="true" />
      <div className="text-sm">
        <p className="font-semibold">{title}</p>
        <p>{children}</p>
      </div>
    </div>
  );
}

type LegacyRateCalloutProps = {
  /** The standard (new-user) price for the selected options; render nothing when undefined. */
  standardCredits: number | undefined;
  variant?: LegacyCalloutVariant;
  className?: string;
};

/**
 * Per-request legacy-rate message shown next to a cost estimate (API
 * Playground, DataPipeline, Crawler). Copy is the Linear issue text.
 */
export function LegacyRateCallout({ standardCredits, variant = "box", className }: LegacyRateCalloutProps) {
  if (standardCredits === undefined) return null;
  const pluralS = standardCredits === 1 ? "" : "s";
  return (
    <LegacyCallout title="Legacy rate:" variant={variant} className={className} testId="legacyRateCallout">
      You're on the old rate for this domain. If you cancel your paid subscription, it will cost{" "}
      <span className="font-semibold">{standardCredits} credit{pluralS}/request</span>.
    </LegacyCallout>
  );
}

/**
 * Account-level legacy-pricing note for the billing Current Plan card. The
 * numbers come from LEGACY_PRICING_STANDARD_RATES because there is no domain
 * to quote here.
 */
export function LegacyPricingNote({ className }: { className?: string }) {
  const { akamai, ebayHtml } = LEGACY_PRICING_STANDARD_RATES;
  return (
    <LegacyCallout title="Legacy pricing" className={className} testId="legacyPricingNote">
      You're on legacy rates for Akamai-protected domains and eBay HTML. If you cancel and resubscribe, requests
      will cost {akamai} credits for Akamai-protected domains and {ebayHtml} credits for eBay HTML.
    </LegacyCallout>
  );
}
